import { ChevronDown, Sparkles } from "lucide-react";

import type {
  BrokerSnapshot,
  ProtocolSnapshot,
} from "@stonkland/protocol/api-types";
import {
  amountLabel,
  assetColor,
  decimalLabel,
} from "@web/features/terminal/presentation";

type PersonalRewardsViewProps = {
  broker?: BrokerSnapshot;
  metrics?: NonNullable<ProtocolSnapshot["metrics"]>;
  ownedTokens: bigint[];
  selectedToken?: bigint;
  onSelectToken: (tokenId: bigint) => void;
};

export function PersonalRewardsView({
  broker,
  metrics,
  ownedTokens,
  selectedToken,
  onSelectToken,
}: PersonalRewardsViewProps) {
  const brokerWeight = broker ? BigInt(broker.activation.weight) : 0n;
  const activeWeight = metrics ? BigInt(metrics.activeWeight) : 0n;
  const pot = metrics ? BigInt(metrics.clockInPot) : 0n;
  const sharePpm =
    brokerWeight > 0n && activeWeight > 0n
      ? (brokerWeight * 1_000_000n) / activeWeight
      : 0n;
  const estimatedAllocation =
    sharePpm > 0n ? (pot * brokerWeight) / activeWeight : 0n;
  const sharePercent = (Number(sharePpm) / 10_000).toLocaleString(undefined, {
    maximumFractionDigits: 4,
  });

  if (!ownedTokens.length) {
    return (
      <section className="personal-rewards-empty panel">
        <Sparkles size={24} />
        <h2>No Broker connected to this wallet</h2>
        <p>Buy a Broker, then put it to work before tracking emissions.</p>
        <a className="primary-action" href="/app/trade#buy-land">
          START WITH LAND
        </a>
      </section>
    );
  }

  return (
    <section className="personal-rewards panel">
      <div className="personal-rewards-head">
        <div>
          <span className="section-kicker">REWARDS STAY INSIDE THE NFT</span>
          <h2>Your Broker rewards</h2>
          <p>
            RWA tokens are delivered directly into each working Broker. They
            move with the Broker if it is sold or transferred.
          </p>
        </div>
        <label className="field reward-broker-select">
          <span>BROKER</span>
          <select
            value={selectedToken?.toString() || ""}
            onChange={(event) => onSelectToken(BigInt(event.target.value))}
          >
            {ownedTokens.map((tokenId) => (
              <option key={tokenId.toString()} value={tokenId.toString()}>
                Broker #{tokenId.toString().padStart(4, "0")}
              </option>
            ))}
          </select>
          <ChevronDown size={15} />
        </label>
      </div>

      <div className="reward-estimate-grid">
        <article>
          <span>WORK STATUS</span>
          <strong>
            {broker?.activation.tier
              ? `WORKING · TIER ${broker.activation.tier}`
              : "NOT WORKING"}
          </strong>
          <small>
            {broker?.activation.tier
              ? `${Number(broker.activation.weight) / 10_000}× earning power`
              : "Put this Broker to work to join future emissions."}
          </small>
        </article>
        <article>
          <span>ESTIMATED NEXT-ROUND SHARE</span>
          <strong>{sharePpm > 0n ? `${sharePercent}%` : "—"}</strong>
          <small>Your Broker weight ÷ total active weight.</small>
        </article>
        <article>
          <span>CURRENT POT ALLOCATION</span>
          <strong>
            {sharePpm > 0n ? `${amountLabel(estimatedAllocation, 6)} ETH` : "—"}
          </strong>
          <small>Estimate before RWA swaps, slippage, and round changes.</small>
        </article>
      </div>

      <div className="personal-assets">
        <div className="personal-assets-heading">
          <div>
            <span>ASSETS CURRENTLY INSIDE</span>
            <strong>
              {selectedToken
                ? `BROKER #${selectedToken.toString().padStart(4, "0")}`
                : "SELECT A BROKER"}
            </strong>
          </div>
          <small>ONCHAIN BALANCES</small>
        </div>
        {broker?.assets.length ? (
          broker.assets.map((asset) => (
            <div className="personal-asset-row" key={asset.address}>
              <span>
                <i style={{ background: assetColor(asset.address) }} />
                <b>{asset.symbol}</b>
                <small>{asset.name}</small>
              </span>
              <strong>{decimalLabel(asset.formattedBalance)}</strong>
            </div>
          ))
        ) : (
          <div className="empty-state personal-assets-empty">
            {broker?.activation.tier
              ? "No RWA emissions have reached this Broker yet."
              : "This Broker must be put to work before it can receive emissions."}
          </div>
        )}
      </div>

      <p className="reward-estimate-note">
        Estimates use the current pot and active weights. They are not an APY
        or a promise of future value; both may change before a round executes.
      </p>
    </section>
  );
}
